import { useParams, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { api, type Message } from '../services/api'
import { formatDistanceToNow } from 'date-fns'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export default function SessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  // Fetch session info
  const { data: sessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.getSessions('lifetime'),
  })

  const session = sessions?.find(s => s.session_id === sessionId)

  // Fetch messages
  const { data: messages, isLoading, error } = useQuery({
    queryKey: ['session-messages', sessionId],
    queryFn: async () => {
      if (!session) throw new Error('Session not found')
      console.log('Fetching messages for session:', session.session_id, 'user:', session.user_id)
      try {
        const result = await api.getSessionMessages(session.session_id, session.user_id)
        console.log('Messages received:', result)
        return result
      } catch (err) {
        console.error('Error fetching messages:', err)
        throw err
      }
    },
    enabled: !!session,
  })

  if (!sessionId) {
    return (
      <div className="p-8">
        <div className="alert alert-error">
          <span>Invalid session ID</span>
        </div>
      </div>
    )
  }

  if (isLoading || !sessions) {
    return (
      <div className="p-8">
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="p-8">
        <div className="alert alert-error">
          <span>Session not found</span>
        </div>
        <button className="btn btn-primary mt-4" onClick={() => navigate('/sessions')}>
          Back to Sessions
        </button>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <button className="btn btn-ghost mb-4" onClick={() => navigate('/sessions')}>
          ← Back to Sessions
        </button>
        <div className="alert alert-error">
          <span>{error instanceof Error ? error.message : 'Failed to load messages'}</span>
        </div>
      </div>
    )
  }

  const userMessageCount = messages?.filter(m => m.role === 'user').length || 0
  const assistantMessageCount = messages?.filter(m => m.role === 'assistant').length || 0

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <button className="btn btn-ghost mb-4" onClick={() => navigate('/sessions')}>
          ← Back to Sessions
        </button>

        <div className="flex justify-between items-start">
          <div>
            <h1 className="page-title">{session.title || 'Untitled Session'}</h1>
            <div className="text-sm opacity-70 mt-2">
              <span className="font-medium">{session.user_name || 'Unknown'}</span>
              {session.user_email && <span> • {session.user_email}</span>}
            </div>
          </div>
          <div className="stats shadow">
            <div className="stat">
              <div className="stat-title">Messages</div>
              <div className="stat-value text-primary">{session.message_count || 0}</div>
              <div className="stat-desc">{userMessageCount} User • {assistantMessageCount} AI</div>
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-4 text-sm opacity-70">
          <div>Created {formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}</div>
          <div>Updated {formatDistanceToNow(new Date(session.updated_at), { addSuffix: true })}</div>
        </div>
      </div>

      {/* Messages */}
      <div className="card bg-base-200 shadow-xl">
        <div className="card-body">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="card bg-base-100 shadow-xl max-w-md border border-base-300">
                <div className="card-body items-center text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-error mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h2 className="card-title text-base-content">Failed to load messages</h2>
                  <p className="text-base-content opacity-80">
                    {(error as Error)?.message || 'Unknown error'}
                  </p>
                  <div className="card-actions mt-4">
                    <button className="btn btn-primary" onClick={() => window.location.reload()}>
                      Try Again
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : messages && messages.length > 0 ? (
            <div className="space-y-6">
              {messages.map((message: Message) => (
                <div
                  key={message.message_id}
                  className={`chat ${message.role === 'user' ? 'chat-start' : 'chat-end'}`}
                >
                  <div className="chat-header mb-1">
                    {message.role === 'user' ? 'User' : 'Assistant'}
                    <time className="text-xs opacity-50 ml-2">
                      {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                    </time>
                  </div>
                  <div
                    className={`chat-bubble ${
                      message.role === 'user' ? 'chat-bubble-primary' : 'chat-bubble-secondary'
                    } whitespace-pre-wrap max-w-2xl`}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 opacity-60">
              <p>No messages in this session</p>
              <p className="text-xs mt-2">Session ID: {sessionId}</p>
              <p className="text-xs">User ID: {session.user_id}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
